import { Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";
import { IconButton, Popover } from "@mui/material";
import { get } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SingleValue } from "react-select";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useClearStaleRideEditDto,
  useRidesActions,
  useRidesListActions,
  useRtl,
} from "@ryde/redux";
import {
  ComputedDataKey,
  errors,
  getIsDisabledByCondition,
  IDropdownItem,
  IRideDto,
  isValidPositiveNumber,
  Log,
  METADATA_KEY,
  NO_VALUE_DISPLAY,
  profilePermissionsSelector,
  toSelectListItem,
  useHasPermissionCustomField,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { IRenderColumnTemplate } from "../_core";
import { FramerTooltip } from "../../../common";
import { RideEditableDocksDropdownSelect } from "./ride-docks-editable-dropdown-select";
import {
  RideEditableDocksDropdownPopoverContainer,
  RideEditableDocksDropdownPopoverFooter,
  RideEditableDocksDropdownTemplateStyled,
} from "./ride-docks-editable-dropdown.styled";

export const RideEditableDocksDropdownTemplate: FC<IRenderColumnTemplate> = ({
  value,
  e2eData,
  gridAttributes,
  rowOriginal,
  name,
  readPermission,
  updatePermission,
}) => {
  const ride = rowOriginal as IRideDto;
  const { t } = useTranslation();
  const [dropdownItems, setDropdownItems] = useState<IDropdownItem[]>([]);
  const [lastValue, setLastValue] = useState<number | null>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const { updateRide } = useRidesActions();
  const { clearStaleRideEditDtoCacheForRide } = useClearStaleRideEditDto();
  const roleDataItem = useRecoilValue(profilePermissionsSelector);
  const { getFieldPermissions } = useHasPermissionCustomField();
  const { replaceRide } = useRidesListActions();
  const { isRtl } = useRtl();

  const isDisabledByCondition = getIsDisabledByCondition({
    fieldValues: ride,
    conditions: gridAttributes?.disableCondition,
    permissions: roleDataItem?.permissions,
  });

  const rideComputedData = get(
    ride,
    ComputedDataKey,
  ) as IRideDto["computedData"];

  const { isFieldVisible, isFieldDisabled } = useMemo(
    () =>
      getFieldPermissions({
        readPermission,
        updatePermission,
      }),
    [getFieldPermissions, readPermission, updatePermission],
  );

  const handleClick = useEvent(event => {
    setAnchorEl(event.currentTarget);
  });
  const handleClose = useEvent(() => {
    setAnchorEl(null);
  });

  useEffect(() => {
    const siteAvailableDocksCount =
      rideComputedData?.siteAvailableDocksCount ?? 0;
    if (!siteAvailableDocksCount) {
      return;
    }

    setDropdownItems(
      Array.from({ length: siteAvailableDocksCount }, (_, i) =>
        toSelectListItem({ id: i + 1, name: String(i + 1) }),
      ),
    );
  }, [rideComputedData?.siteAvailableDocksCount]);

  const updateValue = useEvent(async (newValue: number | null) => {
    const updateResultPromise = updateRide({
      rideId: ride?.id as number,
      rideForApiSend: {
        metadata: {
          dock: newValue,
        },
      },
    });

    handleClose();

    try {
      const result = await updateResultPromise;

      if (!result.success) {
        warnAndToastSimpleResult(result, t);
      } else if (result.data) {
        replaceRide({
          ...ride,
          [METADATA_KEY]: {
            ...(ride[METADATA_KEY] || {}),
            dock: newValue,
          },
        });
        const rideId = ride?.id;
        if (isValidPositiveNumber(rideId)) {
          clearStaleRideEditDtoCacheForRide(rideId);
        }
      }
    } catch (error) {
      Log.warnAndToast(t(errors.ride.errorUpdatingRideData), error);
    }
  });

  const handleOnChange = useEvent((newValue: SingleValue<IDropdownItem>) => {
    setLastValue(null);
    updateValue(newValue?.value as number);
  });

  const handleClear = useEvent((): void => {
    if (!lastValue) {
      setLastValue(get(ride, name));
    }
    updateValue(null);
  });

  return isFieldVisible ? (
    <RideEditableDocksDropdownTemplateStyled
      className="cell_simple"
      data-e2e-custom={e2eData}
      data-e2e-type="dropdown-bound-cell-template"
    >
      {!isDisabledByCondition && (
        <IconButton
          onClick={handleClick}
          size="small"
          disabled={isFieldDisabled}
        >
          <EditIcon className="edit-icon" />
        </IconButton>
      )}

      <FramerTooltip content={value} rtlStrippedBottomLeftPlacement>
        {value || NO_VALUE_DISPLAY}
      </FramerTooltip>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isRtl ? "right" : "left",
        }}
      >
        <RideEditableDocksDropdownPopoverContainer>
          <RideEditableDocksDropdownSelect
            options={dropdownItems}
            selectedValue={value}
            onChange={handleOnChange}
          />
        </RideEditableDocksDropdownPopoverContainer>
        <RideEditableDocksDropdownPopoverFooter>
          <button className="clear-button" onClick={handleClear}>
            <CloseIcon className="clear-icon" />
            <span className="clear-text">{t("general.CLEAR")}</span>
          </button>
        </RideEditableDocksDropdownPopoverFooter>
      </Popover>
    </RideEditableDocksDropdownTemplateStyled>
  ) : null;
};
